/**
 * The one query cache, in its own module so that signing out can empty it.
 *
 * It used to be built in `main.tsx`, which meant nothing outside the React tree could
 * reach it: `main.tsx` imports the auth store, so the store could not import back without
 * a cycle. The cache therefore survived a sign-out, and the next person to sign in on the
 * same tab saw the previous user's rows -- their customers, their invoices, the branding
 * of a company they may not be allowed to read -- until something refetched or the page
 * was reloaded. Every query in the app is keyed by what was asked for and never by who
 * asked, so there is no version of that which is safe.
 */

import { QueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // 401 and 403 are answers, not failures. Retrying a permission denial three times
      // just makes the screen slower before showing the same message.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status < 500 ? false : failureCount < 1,
    },
  },
})
